class ScarfholdProductGridUX {
  static observer = null;
  static layoutObserver = null;
  static headerObserver = null;
  static headerOffsetFrame = null;
  static headerOffsetUntil = 0;
  static loadingNextPage = false;
  static previousCardRects = new Map();
  static cardAnimations = new Set();
  static transitionGeneration = 0;

  static get container() {
    return document.getElementById('ProductGridContainer');
  }

  static get selectors() {
    return document.getElementById('ScarfholdFilterSelectors');
  }

  static selectorStateKey(selectors = this.selectors) {
    return selectors?.dataset.sectionId
      ? `scarfhold:selector-stack:${selectors.dataset.sectionId}:${window.location.pathname}`
      : null;
  }

  static storedSelectorState(key) {
    if (!key) return null;
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  static setSelectorCollapsed(collapsed, persist = false) {
    const selectors = this.selectors;
    if (!selectors?.classList.contains('scarfhold-filter-selectors--has-collapse-control')) return;
    selectors.classList.toggle('is-collapsed', collapsed);
    const button = selectors.querySelector('[data-scarfhold-selector-collapse]');
    button?.setAttribute('aria-expanded', String(!collapsed));
    const label = button?.querySelector('[data-scarfhold-selector-collapse-label]');
    if (label) label.textContent = collapsed ? 'Expand visual selectors' : 'Collapse visual selectors';
    if (persist) {
      const key = this.selectorStateKey(selectors);
      try {
        if (key) sessionStorage.setItem(key, collapsed ? 'collapsed' : 'expanded');
      } catch (error) {
        // The control still works when session storage is unavailable.
      }
    }
    this.updateLayoutMetrics();
  }

  static initSelectorCollapse() {
    const selectors = this.selectors;
    if (!selectors?.classList.contains('scarfhold-filter-selectors--has-collapse-control')) return;
    const key = this.selectorStateKey(selectors);
    const stored = this.storedSelectorState(key);
    const collapsed = stored
      ? stored === 'collapsed'
      : selectors.dataset.defaultSelectorState === 'collapsed';
    this.setSelectorCollapsed(collapsed);
  }

  static beginFilterTransition() {
    const container = this.container;
    if (!container || container.dataset.filterTransition === 'none') return;
    this.transitionGeneration += 1;
    this.finishCardAnimations();
    this.previousCardRects =
      container.dataset.filterTransition === 'fade_move' && !this.prefersReducedMotion()
        ? this.captureCardRects(container)
        : new Map();
    container.classList.remove('is-filter-entering');
    container.classList.add('is-filter-transitioning');
  }

  static completeFilterTransition() {
    const container = this.container;
    if (!container) return;
    container.classList.remove('is-filter-transitioning');
    if (container.dataset.filterTransition === 'none') return;
    if (container.dataset.filterTransition === 'fade_move') {
      if (!this.prefersReducedMotion())
        this.animateReflow(container, this.previousCardRects, this.transitionGeneration);
      this.previousCardRects = new Map();
      return;
    }
    container.classList.add('is-filter-entering');
    requestAnimationFrame(() => requestAnimationFrame(() => container.classList.remove('is-filter-entering')));
  }

  static cancelFilterTransition() {
    const container = this.container;
    if (!container) return;
    this.finishCardAnimations();
    this.transitionGeneration += 1;
    this.previousCardRects = new Map();
    container.classList.remove('is-filter-transitioning', 'is-filter-entering');
  }

  static prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  static cardEntries(container = this.container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll('.grid__item[data-product-id]')).flatMap((item) => {
      const reflowElement = item.querySelector('.scarfhold-product-grid-reflow');
      return reflowElement ? [[item.dataset.productId, reflowElement]] : [];
    });
  }

  static captureCardRects(container = this.container) {
    return new Map(this.cardEntries(container).map(([id, element]) => [id, element.getBoundingClientRect()]));
  }

  static transitionDuration(container = this.container) {
    const rawDuration = container
      ? getComputedStyle(container).getPropertyValue('--scarfhold-filter-transition-duration')
      : '';
    return Number.parseFloat(rawDuration) || 400;
  }

  static entranceDistance(container = this.container) {
    const rawDistance = container
      ? getComputedStyle(container).getPropertyValue('--scarfhold-filter-move-distance')
      : '';
    return Number.parseFloat(rawDistance) || 0;
  }

  static trackCardAnimation(animation, element) {
    this.cardAnimations.add(animation);
    element.style.willChange = 'transform, opacity';
    const clean = () => {
      this.cardAnimations.delete(animation);
      element.style.willChange = '';
    };
    animation.addEventListener('finish', clean, { once: true });
    animation.addEventListener('cancel', clean, { once: true });
  }

  static finishCardAnimations() {
    this.cardAnimations.forEach((animation) => {
      try {
        animation.finish();
      } catch (error) {
        animation.cancel();
      }
    });
    this.cardAnimations.clear();
    this.container
      ?.querySelectorAll('.scarfhold-product-grid-reflow')
      .forEach((element) => (element.style.willChange = ''));
  }

  static animateReflow(container, previousRects, generation) {
    const duration = this.transitionDuration(container);
    const entranceDistance = this.entranceDistance(container);
    const entries = this.cardEntries(container);

    requestAnimationFrame(() => {
      if (generation !== this.transitionGeneration || container !== this.container) return;
      entries.forEach(([id, element]) => {
        const previousRect = previousRects.get(id);
        if (previousRect) {
          const currentRect = element.getBoundingClientRect();
          const deltaX = previousRect.left - currentRect.left;
          const deltaY = previousRect.top - currentRect.top;
          if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) return;
          const animation = element.animate(
            [
              { transform: `translate(${deltaX}px, ${deltaY}px)` },
              { transform: 'translate(0, 0)' },
            ],
            { duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
          );
          this.trackCardAnimation(animation, element);
          return;
        }

        const animation = element.animate(
          [
            { opacity: 0, transform: `translateY(${entranceDistance}px)` },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration, easing: 'ease-out' }
        );
        this.trackCardAnimation(animation, element);
      });
    });
  }

  static animateAppendedCards(elements, container = this.container) {
    if (
      !container ||
      container.dataset.filterTransition !== 'fade_move' ||
      this.prefersReducedMotion() ||
      !elements.length
    )
      return;
    const duration = this.transitionDuration(container);
    const entranceDistance = this.entranceDistance(container);
    requestAnimationFrame(() => {
      elements.forEach((element) => {
        const animation = element.animate(
          [
            { opacity: 0, transform: `translateY(${entranceDistance}px)` },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration, easing: 'ease-out' }
        );
        this.trackCardAnimation(animation, element);
      });
    });
  }

  static updateLayoutMetrics() {
    const section = document.querySelector('.scarfhold-product-grid-section');
    const selectors = section?.querySelector('.scarfhold-filter-selectors');
    if (!section || !selectors) return;

    section.style.setProperty('--scarfhold-sticky-selectors-height', `${selectors.offsetHeight}px`);
    selectors.querySelectorAll('.scarfhold-filter-selector--separator-content').forEach((selector) => {
      const items = Array.from(selector.querySelectorAll('.scarfhold-filter-selector__item')).filter(
        (item) => item.getClientRects().length > 0
      );
      if (!items.length) {
        selector.style.setProperty('--scarfhold-separator-content-left', '0px');
        selector.style.setProperty('--scarfhold-separator-content-width', '0px');
        return;
      }
      const selectorRect = selector.getBoundingClientRect();
      const left = Math.min(...items.map((item) => item.getBoundingClientRect().left));
      const right = Math.max(...items.map((item) => item.getBoundingClientRect().right));
      selector.style.setProperty('--scarfhold-separator-content-left', `${left - selectorRect.left}px`);
      selector.style.setProperty('--scarfhold-separator-content-width', `${right - left}px`);
    });
  }

  static initLayoutMetrics() {
    this.layoutObserver?.disconnect();
    this.initSelectorCollapse();
    this.updateLayoutMetrics();
    if (!('ResizeObserver' in window)) return;
    const selectors = document.querySelector('.scarfhold-filter-selectors');
    if (!selectors) return;
    this.layoutObserver = new ResizeObserver(() => this.updateLayoutMetrics());
    this.layoutObserver.observe(selectors);
  }

  static updateVisibleHeaderOffset() {
    const section = document.querySelector('.scarfhold-product-grid-section');
    const header = document.querySelector('.section-header');
    if (!section || !header) return;
    const rect = header.getBoundingClientRect();
    const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    section.style.setProperty('--scarfhold-visible-header-height', `${visibleHeight}px`);
  }

  static trackHeaderOffset(duration = 0) {
    this.headerOffsetUntil = Math.max(this.headerOffsetUntil, performance.now() + duration);
    if (this.headerOffsetFrame) return;
    const update = () => {
      this.updateVisibleHeaderOffset();
      if (performance.now() < this.headerOffsetUntil) {
        this.headerOffsetFrame = requestAnimationFrame(update);
      } else {
        this.headerOffsetFrame = null;
      }
    };
    this.headerOffsetFrame = requestAnimationFrame(update);
  }

  static initStickyHeaderOffset() {
    const header = document.querySelector('.section-header');
    if (!header) return;
    this.headerObserver?.disconnect();
    this.headerObserver = new MutationObserver(() => this.trackHeaderOffset(220));
    this.headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('scroll', () => this.trackHeaderOffset(180), { passive: true });
    window.addEventListener('resize', () => this.trackHeaderOffset(220), { passive: true });
    this.trackHeaderOffset(220);
  }

  static initInfiniteScroll() {
    if (this.observer) this.observer.disconnect();
    this.observer = null;
    this.loadingNextPage = false;

    const container = this.container;
    if (!container) return;
    container.classList.remove('is-infinite-scroll-ready');
    if (container.dataset.paginationMode !== 'infinite' || !('IntersectionObserver' in window)) return;

    const pagination = container.querySelector('.pagination-wrapper');
    const nextLink = pagination?.querySelector('.pagination__item--prev[href]');
    if (!pagination || !nextLink) return;

    container.classList.add('is-infinite-scroll-ready');
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(pagination);
  }

  static async loadNextPage() {
    if (this.loadingNextPage) return;
    const container = this.container;
    const pagination = container?.querySelector('.pagination-wrapper');
    const nextLink = pagination?.querySelector('.pagination__item--prev[href]');
    if (!container || !pagination || !nextLink) return;

    this.loadingNextPage = true;
    this.observer?.disconnect();
    const status = container.querySelector('.scarfhold-infinite-scroll-status');
    if (status) status.hidden = false;
    let loaded = false;

    try {
      const nextURL = new URL(nextLink.href, window.location.origin);
      const sectionId = document.getElementById('product-grid')?.dataset.id;
      if (sectionId) nextURL.searchParams.set('section_id', sectionId);
      const response = await fetch(nextURL.toString());
      if (!response.ok) throw new Error(`Product page request failed: ${response.status}`);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = parsed.getElementById('product-grid');
      const currentGrid = container.querySelector('#product-grid');
      if (!incomingGrid || !currentGrid) throw new Error('Product grid missing from next page');

      const existingIds = new Set(Array.from(currentGrid.children, (item) => item.dataset.productId));
      const appendedCards = [];
      Array.from(incomingGrid.children).forEach((item) => {
        if (!item.dataset.productId || existingIds.has(item.dataset.productId)) return;
        existingIds.add(item.dataset.productId);
        item.classList.add('scroll-trigger--cancel');
        currentGrid.appendChild(item);
        const reflowElement = item.querySelector('.scarfhold-product-grid-reflow');
        if (reflowElement) appendedCards.push(reflowElement);
      });
      this.animateAppendedCards(appendedCards, container);

      const incomingPagination = parsed.querySelector('#ProductGridContainer .pagination-wrapper');
      if (incomingPagination) {
        pagination.replaceWith(incomingPagination);
      } else {
        pagination.remove();
      }
      loaded = true;
    } catch (error) {
      console.error('Scarfhold infinite scroll:', error);
      container.classList.remove('is-infinite-scroll-ready');
    } finally {
      if (status) status.hidden = true;
      this.loadingNextPage = false;
      if (loaded) this.initInfiniteScroll();
    }
  }
}

window.ScarfholdProductGridUX = ScarfholdProductGridUX;
document.addEventListener('DOMContentLoaded', () => {
  ScarfholdProductGridUX.initInfiniteScroll();
  ScarfholdProductGridUX.initLayoutMetrics();
  ScarfholdProductGridUX.initStickyHeaderOffset();
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-scarfhold-selector-collapse]');
  if (!button) return;
  const selectors = button.closest('.scarfhold-filter-selectors');
  ScarfholdProductGridUX.setSelectorCollapsed(!selectors.classList.contains('is-collapsed'), true);
});
